import {
  type AcceptAiTagsResult,
  type BulkRatingResult,
  type BulkTagResult,
  type BulkUntagResult,
  type Rating,
  type SelectionTagsResult,
  acceptAiTagsRequestSchema,
  acceptAiTagsResultSchema,
  bulkRatingRequestSchema,
  bulkRatingResultSchema,
  bulkTagRequestSchema,
  bulkTagResultSchema,
  bulkUntagRequestSchema,
  bulkUntagResultSchema,
  downloadSelectionRequestSchema,
  selectionTagsRequestSchema,
  selectionTagsResultSchema,
} from '@xphoto/shared';
import { ApiError } from './api';
import { NO_TIMEOUT, apiFetch } from '../lib/http';

/**
 * Client typé des ACTIONS DE MASSE sur une sélection (LOT 3 : notation de masse ;
 * LOT 4 : ajout de tags existants en masse). Comme les autres clients, la RÉPONSE est
 * re-parsée via son schéma partagé (« never trust the client », y compris ses propres
 * réponses) et le corps est validé en amont par son schéma de requête.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/**
 * `POST /api/libraries/:id/assets/bulk-rating` — fixe la note `rating` (0..5, 0 = non
 * noté) sur une SÉLECTION d'assets d'un coup (mutation DB synchrone). Renvoie
 * `{ updated }` (nombre réellement mis à jour). 403 (grantee lecture) → `ApiError`.
 */
export async function setRatingForSelection(
  libraryId: string,
  assetIds: readonly string[],
  rating: Rating,
): Promise<BulkRatingResult> {
  const body = bulkRatingRequestSchema.parse({ assetIds: [...assetIds], rating });
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/bulk-rating`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return bulkRatingResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/assets/bulk-tags` — ajoute des tags EXISTANTS (par leur
 * `id`) à une SÉLECTION d'assets d'un coup (mutation DB synchrone). Renvoie
 * `{ linked }` (nombre de liaisons créées ou promues). Le backend borne tout à la
 * bibliothèque et exclut les assets supprimés. 403 (grantee lecture) → `ApiError`.
 */
export async function addTagsToSelection(
  libraryId: string,
  assetIds: readonly string[],
  tagIds: readonly string[],
): Promise<BulkTagResult> {
  const body = bulkTagRequestSchema.parse({ assetIds: [...assetIds], tagIds: [...tagIds] });
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/bulk-tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return bulkTagResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/assets/bulk-untags` — RETIRE des tags EXISTANTS (par leur `id`)
 * d'une SÉLECTION d'assets d'un coup (mode « Retirer » de la modale). Renvoie
 * `{ unlinked }` (nombre de liaisons supprimées). 403 (grantee lecture) → `ApiError`.
 */
export async function removeTagsFromSelection(
  libraryId: string,
  assetIds: readonly string[],
  tagIds: readonly string[],
): Promise<BulkUntagResult> {
  const body = bulkUntagRequestSchema.parse({ assetIds: [...assetIds], tagIds: [...tagIds] });
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/bulk-untags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return bulkUntagResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/assets/selection-tags` — liste les tags VALIDÉS présents sur une
 * SÉLECTION d'assets (avec, pour chacun, le nombre d'assets qui le portent). Alimente le
 * mode « Retirer » de la modale de tags. LECTURE seule. 403 (hors périmètre) → `ApiError`.
 */
export async function fetchSelectionTags(
  libraryId: string,
  assetIds: readonly string[],
  signal?: AbortSignal,
): Promise<SelectionTagsResult> {
  const body = selectionTagsRequestSchema.parse({ assetIds: [...assetIds] });
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/selection-tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: signal ?? null,
  });
  return selectionTagsResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/assets/accept-ai-tags` — ACCEPTE en masse les suggestions IA
 * (`proposed`) d'une SÉLECTION d'assets → les promeut en `validated` (mutation DB
 * synchrone). NE TOUCHE PAS `assets.tagged` (les photos restent « À valider (IA) »).
 * Renvoie `{ accepted }` (liaisons promues). 403 (grantee lecture) → `ApiError`.
 */
export async function acceptAiTagsForSelection(
  libraryId: string,
  assetIds: readonly string[],
): Promise<AcceptAiTagsResult> {
  const body = acceptAiTagsRequestSchema.parse({ assetIds: [...assetIds] });
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/accept-ai-tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return acceptAiTagsResultSchema.parse(await readJson(res));
}

/**
 * `POST /api/libraries/:id/assets/download` — télécharge les ORIGINAUX d'une SÉLECTION
 * d'assets dans une seule archive ZIP. Renvoie le `Blob` `application/zip` (que
 * l'appelant sauvegarde via un `<a download>`). Sur échec, lève un `ApiError` (403
 * grantee lecture → code `FORBIDDEN` ; le corps d'erreur est JSON même si la réponse OK
 * est binaire).
 *
 * ⚠ AUCUN plafond de durée (`NO_TIMEOUT`) : le serveur prépare l'archive PUIS la
 * streame — la durée dépend du nombre et du poids des originaux sélectionnés (des
 * dizaines de Go possibles) et du débit descendant. Aucune borne fixe n'est défendable.
 */
export async function downloadSelectionZip(
  libraryId: string,
  assetIds: readonly string[],
): Promise<Blob> {
  const body = downloadSelectionRequestSchema.parse({ assetIds: [...assetIds] });
  const res = await apiFetch(`/api/libraries/${libraryId}/assets/download`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    timeoutMs: NO_TIMEOUT,
  });
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const err = (await res.json()) as { error?: { code?: string } };
      if (typeof err?.error?.code === 'string') code = err.error.code;
    } catch {
      // Corps non-JSON : code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.blob();
}
